import { Outlet, RouteObject } from '@rocketlane/rocket-router';
import React, { FC, Suspense, lazy } from 'react';
import { CreditNoteEssentialSettings } from 'components/organisms/credit-notes-settings/essential-setting';
import { AntdRlUISwitcherProvider } from 'contexts/antd-rlui-switcher';
import { useIsScarletFeatureEnabled } from 'hooks/use-scarlet-feature';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { KnowledgeBaseFeatureWrapper } from './scarlet-wrapper';

const EmailSettingsPage = lazy(() =>
  import('./email-intelligence/email-settings').then((m) => ({
    default: m.EmailSettingsPage,
  })),
);
const NitroCreditConsumptionPage = lazy(() =>
  import('./nitro-credit-consumption/page').then((m) => ({
    default: m.NitroCreditConsumptionPage,
  })),
);
const BlacklistedDomainsPage = lazy(() =>
  import('./email-intelligence/blacklisted-domains').then((m) => ({
    default: m.BlacklistedDomainsPage,
  })),
);

// Lazy load all page components
const SignalConfigurationsPage = lazy(() =>
  import('./signal-configurations/page').then((m) => ({
    default: m.SignalConfigurationsPage,
  })),
);

const PostingPeriods = lazy(() =>
  import(
    'components/organisms/module-settings/revenue-entries/revenue-settings'
  ).then((m) => ({
    default: m.PostingPeriods,
  })),
);
const RevenueEssentials = lazy(() =>
  import(
    'components/organisms/module-settings/revenue-entries/revenue-settings'
  ).then((m) => ({
    default: m.RevenueEssentials,
  })),
);

const BudgetFieldsSettingsPage = lazy(() =>
  import('./budget-fields/budget-fields').then((m) => ({
    default: m.BudgetFieldsSettingsPage,
  })),
);

const ScimPage = lazy(() =>
  import('./scim').then((m) => ({ default: m.ScimPage })),
);

const ExpenseApprovalSettings = lazy(() =>
  import(
    'components/organisms/expense-settings/approval-workflow/approval-settings'
  ).then((m) => ({ default: m.ExpenseApprovalSettings })),
);

const InvoiceApprovalSettings = lazy(() =>
  import(
    'components/organisms/invoice-settings/approval-workflow/approval-settings'
  ).then((m) => ({ default: m.InvoiceApprovalSettings })),
);
const BillingScheduleTemplatesListPage = lazy(() =>
  import('components/organisms/invoice-settings/billing-schedule/list').then(
    (m) => ({ default: m.BillingScheduleTemplatesListPage }),
  ),
);
const ExistingBillingScheduleTemplatePage = lazy(() =>
  import(
    'components/organisms/invoice-settings/billing-schedule/template-landing'
  ).then((m) => ({ default: m.ExistingBillingScheduleTemplatePage })),
);
const NewBillingScheduleTemplatePage = lazy(() =>
  import(
    'components/organisms/invoice-settings/billing-schedule/template-landing'
  ).then((m) => ({ default: m.NewBillingScheduleTemplatePage })),
);
const BillingScheduleProviderShell = lazy(() =>
  import('./billing-schedule-provider-shell').then((m) => ({
    default: m.BillingScheduleProviderShell,
  })),
);
const InvoicingCustomFields = lazy(() =>
  import(
    'components/organisms/invoice-settings/custom-fields/custom-fields'
  ).then((m) => ({ default: m.InvoicingCustomFields })),
);
const InvoiceEssentialSettings = lazy(() =>
  import('components/organisms/invoice-settings/essential-settings').then(
    (m) => ({ default: m.InvoiceEssentialSettings }),
  ),
);
const TaxSettings = lazy(() =>
  import('components/organisms/invoices/taxes/settings').then((m) => ({
    default: m.TaxSettings,
  })),
);
const TaxCodes = lazy(() =>
  import('components/organisms/invoices/taxes/tax-codes').then((m) => ({
    default: m.TaxCodes,
  })),
);
const AccountAddressSettings = lazy(() =>
  import('components/organisms/module-settings/address/address').then((m) => ({
    default: m.AccountAddressSettings,
  })),
);
const AiFillSettingsPage = lazy(() =>
  import('components/organisms/module-settings/ai-fills').then((m) => ({
    default: m.AiFillSettingsPage,
  })),
);
const BillingSettings = lazy(() =>
  import('components/organisms/module-settings/billing').then((m) => ({
    default: m.BillingSettings,
  })),
);
const CustomDomain = lazy(() =>
  import('components/organisms/module-settings/custom-domain').then((m) => ({
    default: m.CustomDomain,
  })),
);
const CustomerNotificationSettingsPage = lazy(() =>
  import(
    'components/organisms/module-settings/customer-portal-config/customer-notifications'
  ).then((m) => ({ default: m.CustomerNotificationSettingsPage })),
);
const NotificationsConfigPage = lazy(() =>
  import(
    'components/organisms/module-settings/customer-portal-config/customer-notifications'
  ).then((m) => ({ default: m.NotificationsConfigPage })),
);
const ProjectTabsVisibility = lazy(() =>
  import(
    'components/organisms/module-settings/customer-portal-config/project-tab-visibility'
  ).then((m) => ({ default: m.ProjectTabsVisibility })),
);
const EmailConfiguration = lazy(() =>
  import('components/organisms/module-settings/email-configuration').then(
    (m) => ({ default: m.EmailConfiguration }),
  ),
);
const Financials = lazy(() =>
  import('components/organisms/module-settings/financials/financials').then(
    (m) => ({ default: m.Financials }),
  ),
);
const Integrations = lazy(() =>
  import('components/organisms/module-settings/integrations').then((m) => ({
    default: m.Integrations,
  })),
);
const Workato = lazy(() =>
  import(
    'components/organisms/module-settings/integrations/integrations-list'
  ).then((m) => ({ default: m.Workato })),
);
const Zapier = lazy(() =>
  import(
    'components/organisms/module-settings/integrations/integrations-list'
  ).then((m) => ({ default: m.Zapier })),
);
const Jira = lazy(() =>
  import(
    'components/organisms/module-settings/integrations/integrations-list/jira/jira'
  ).then((m) => ({ default: m.Jira })),
);
const KeyeventsAndIntervalsSettings = lazy(() =>
  import(
    'components/organisms/module-settings/key-events-and-intervals/settings'
  ).then((m) => ({ default: m.KeyeventsAndIntervalsSettings })),
);
const NotificationSettingsPage = lazy(() =>
  import('components/organisms/module-settings/notifications').then((m) => ({
    default: m.NotificationSettingsPage,
  })),
);
const ProfileSettingsPage = lazy(() =>
  import('components/organisms/module-settings/profile').then((m) => ({
    default: m.ProfileSettingsPage,
  })),
);
const ResourceManagementSettings = lazy(() =>
  import('components/organisms/module-settings/resource-management').then(
    (m) => ({ default: m.ResourceManagementSettings }),
  ),
);
const SkillMatrixSettingsPage = lazy(() =>
  import(
    'components/organisms/module-settings/skill-matrix/skill-matrix.page'
  ).then((m) => ({ default: m.SkillMatrixSettingsPage })),
);
const TeamMemberSettingsPage = lazy(() =>
  import('components/organisms/module-settings/team-members/team-members').then(
    (m) => ({ default: m.TeamMemberSettingsPage }),
  ),
);
const AdvancedSettingsPage = lazy(() =>
  import('components/pages').then((m) => ({ default: m.AdvancedSettingsPage })),
);
const ExpenseSettings = lazy(() =>
  import('components/pages/expense-settings').then((m) => ({
    default: m.ExpenseSettings,
  })),
);
const OutOfOffice = lazy(() =>
  import('components/pages/out-of-office').then((m) => ({
    default: m.OutOfOffice,
  })),
);
const PartnerDetail = lazy(() =>
  import('components/pages/partner/detail').then((m) => ({
    default: m.PartnerDetail,
  })),
);
const PartnerList = lazy(() =>
  import('components/pages/partner/list').then((m) => ({
    default: m.PartnerList,
  })),
);
const PartnerTeamMembers = lazy(() =>
  import('components/pages/partner/team-members').then((m) => ({
    default: m.PartnerTeamMembers,
  })),
);
const RocketlaneAPIPage = lazy(() =>
  import('components/pages/rocketlane-api').then((m) => ({
    default: m.RocketlaneAPIPage,
  })),
);
const TimesheetSettings = lazy(() =>
  import('components/pages/timesheet-settings').then((m) => ({
    default: m.TimesheetSettings,
  })),
);
const NotFound = lazy(() =>
  import('routes/404').then((m) => ({ default: m.NotFound })),
);
const CustomerPortalThemeById = lazy(() =>
  import('routes/customer-portal/cpt-theme-by-id').then((m) => ({
    default: m.CustomerPortalThemeById,
  })),
);
const CustomerPortalNotificationSettingsPage = lazy(() =>
  import(
    'routes/customer-portal/customer-portal-notification-settings-page'
  ).then((m) => ({ default: m.CustomerPortalNotificationSettingsPage })),
);
const CustomerPortalThemesList = lazy(() =>
  import('routes/customer-portal/customer-portal-themes-list').then((m) => ({
    default: m.CustomerPortalThemesList,
  })),
);
const CustomerPortalSettings = lazy(() =>
  import('routes/customer-portal/embed-settings-page').then((m) => ({
    default: m.CustomerPortalSettings,
  })),
);
const CustomerPortalInviteSettingsPage = lazy(() =>
  import('routes/customer-portal/invite-config-settings-page').then((m) => ({
    default: m.CustomerPortalInviteSettingsPage,
  })),
);
const NewCustomerPortalTheme = lazy(() =>
  import('routes/customer-portal/new-customer-portal-theme').then((m) => ({
    default: m.NewCustomerPortalTheme,
  })),
);
const ArchivedProjectsPage = lazy(() =>
  import('./archived-projects').then((m) => ({
    default: m.ArchivedProjectsPage,
  })),
);
const CompanyFieldsSettingsPage = lazy(() =>
  import('./company-fields').then((m) => ({
    default: m.CompanyFieldsSettingsPage,
  })),
);
const CurrencySettings = lazy(() =>
  import('./currency').then((m) => ({ default: m.CurrencySettings })),
);
const CustomAppsPage = lazy(() =>
  import('./custom-apps/custom-apps-page').then((m) => ({
    default: m.CustomAppsPage,
  })),
);
const CustomerTeamMemberFieldsSettingsPage = lazy(() =>
  import('./customer-team-member-fields/customer-team-member-fields').then(
    (m) => ({ default: m.CustomerTeamMemberFieldsSettingsPage }),
  ),
);
const ExpenseFieldsSettingsPage = lazy(() =>
  import('./expense-fields/expense-fields').then((m) => ({
    default: m.ExpenseFieldsSettingsPage,
  })),
);
const GlobalAutomationSettings = lazy(() =>
  import('./global-automations').then((m) => ({
    default: m.GlobalAutomationSettings,
  })),
);
const GroupsCreationPage = lazy(() =>
  import('./groups').then((m) => ({ default: m.GroupsCreationPage })),
);
const GroupDetail = lazy(() =>
  import('./groups/GroupDetail').then((m) => ({ default: m.GroupDetail })),
);
const HolidayCalendarSettingPage = lazy(() =>
  import('./holiday-calendar/holiday-calendar-settings-page').then((m) => ({
    default: m.HolidayCalendarSettingPage,
  })),
);
const HubspotIntegrationsLandingPage = lazy(() =>
  import('./integrations/hubspot').then((m) => ({
    default: m.HubspotIntegrationsLandingPage,
  })),
);
const SalesForceIntegrationsLandingPage = lazy(() =>
  import('./integrations/salesforce/salesforce-page').then((m) => ({
    default: m.SalesForceIntegrationsLandingPage,
  })),
);
const SlackLandingPage = lazy(() =>
  import('./integrations/slack/slack-landing-page').then((m) => ({
    default: m.SlackLandingPage,
  })),
);
const LanguageConfigurationsPage = lazy(() =>
  import('./language-configurations/page').then((m) => ({
    default: m.LanguageConfigurationsPage,
  })),
);
const OrganisationSettingPage = lazy(() =>
  import('./organisation').then((m) => ({
    default: m.OrganisationSettingPage,
  })),
);
const PermisionHomePage = lazy(() =>
  import('./permissions').then((m) => ({ default: m.PermisionHomePage })),
);
const PermissionCreationPage = lazy(() =>
  import('./permissions/permission-creation-route').then((m) => ({
    default: m.PermissionCreationPageRoute,
  })),
);
const ProjectFieldsV2SettingsPage = lazy(() =>
  import('./project-fields-v2').then((m) => ({
    default: m.ProjectFieldsV2SettingsPage,
  })),
);
const RateCardsPage = lazy(() =>
  import('./rate-cards').then((m) => ({ default: m.RateCardsPage })),
);
const RolesSettingsPage = lazy(() =>
  import('./roles').then((m) => ({ default: m.RolesSettingsPage })),
);
const SecurityPage = lazy(() =>
  import('./security').then((m) => ({ default: m.SecurityPage })),
);
const SettingsPageLayout = lazy(() =>
  import('./settings-page-layout').then((m) => ({
    default: m.SettingsPageLayout,
  })),
);
const SkillFieldsSettingsPage = lazy(() =>
  import('./skill-fields/skill-fields').then((m) => ({
    default: m.SkillFieldsSettingsPage,
  })),
);
const SprintPlanningSettings = lazy(() =>
  import('./sprint-planning').then((m) => ({
    default: m.SprintPlanningSettings,
  })),
);
const SprintFields = lazy(() =>
  import('./sprint-planning/sprint-fields').then((m) => ({
    default: m.SprintFields,
  })),
);
const TaskFieldsSettingsPage = lazy(() =>
  import('./task-fields').then((m) => ({ default: m.TaskFieldsSettingsPage })),
);
const TeamMemberFieldsSettingsPage = lazy(() =>
  import('./team-member-fields').then((m) => ({
    default: m.TeamMemberFieldsSettingsPage,
  })),
);
const TimeEntryFieldsSettingsPage = lazy(() =>
  import('./time-entry-fields/time-entry-fields').then((m) => ({
    default: m.TimeEntryFieldsSettingsPage,
  })),
);
const EditWebhookPage = lazy(() =>
  import('./webhook/edit-webhook-page').then((m) => ({
    default: m.EditWebhookPage,
  })),
);
const NewWebhookPage = lazy(() =>
  import('./webhook/new-webhook-page').then((m) => ({
    default: m.NewWebhookPage,
  })),
);
const WebhookPage = lazy(() =>
  import('./webhook/webhook').then((m) => ({ default: m.WebhookPage })),
);

const EpicFields = lazy(() =>
  import('./sprint-planning/epic-fields').then((m) => ({
    default: m.EpicFields,
  })),
);

const VaultPage = lazy(() =>
  import('./connection-store').then((m) => ({
    default: m.VaultPage,
  })),
);

const VaultPageWithFeatureFlag: FC = () => {
  const isScarletEnabled = useIsScarletFeatureEnabled();

  if (!isScarletEnabled) {
    return <NotFound />;
  }

  return <VaultPage />;
};
const KnowledgeBasePage = React.lazy(() => import('signals/KnowledgeBasePage'));
const KnowledgeBaseFolderPage = React.lazy(
  () => import('signals/KnowledgeBaseFolderPage'),
);

const AllocationFieldsSettingsPage = lazy(() =>
  import('./allocation-fields/allocation-fields').then((m) => ({
    default: m.AllocationFieldsSettingsPage,
  })),
);

export const settingsRoutes: RouteObject = {
  path: ROUTE_PATHS.SETTINGS,
  element: (
    <>
      <Outlet />
    </>
  ),
  children: [
    {
      path: ROUTE_PATHS.SETTINGS_PROFILE,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ProfileSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_NITRO_CREDIT_CONSUMPTION,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <NitroCreditConsumptionPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_NOTIFICATIONS,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <NotificationSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_NOTIFICATIONS,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOMER_PORTAL:READ">
          <SettingsPageLayout>
            <CustomerPortalNotificationSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_OLD,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOMER_PORTAL:READ">
          <SettingsPageLayout>
            <CustomerNotificationSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_INVITE_CONFIGURATION,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomerPortalInviteSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_EMBED_CUSTOMER_PORTAL,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomerPortalSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_THEMES,
      element: (
        <SecureRoute rule="SETTINGS:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomerPortalThemesList />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_THEMES}/${ROUTE_PATHS.NEW}`,
      element: (
        <SecureRoute rule="SETTINGS:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <NewCustomerPortalTheme />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_THEMES}/${ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_THEME_ID}`,
      element: (
        <SecureRoute rule="SETTINGS:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomerPortalThemeById />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_ORGANISATION,
      element: (
        <SecureRoute rule="SETTINGS_ORGANISATION:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <OrganisationSettingPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_HOLIDAY_CALENDAR,
      element: (
        <SecureRoute rule="SETTINGS_HOLIDAY_CALENDAR:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <HolidayCalendarSettingPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_OUT_OF_OFFICE,
      element: (
        <SecureRoute rule="SETTINGS_OUT_OF_OFFICE:WRITE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <OutOfOffice />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_BILLING,
      element: (
        <SecureRoute rule="BILLING:READ">
          <SettingsPageLayout checkIfUserHasAccessToBilling>
            <AntdRlUISwitcherProvider>
              <BillingSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_TEAM_MEMBERS,
      element: (
        <SecureRoute rule="SETTINGS_TEAM_MEMBER:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TeamMemberSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_CUSTOMER_TEAM_MEMBER_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOMER_TEAM_MEMBER_FIELD:READ">
          <SettingsPageLayout checkIfCustomerPortalIsEnabled>
            <AntdRlUISwitcherProvider>
              <CustomerTeamMemberFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_SKILL_MATRIX}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_SKILL_MATRIX:READ">
          <AntdRlUISwitcherProvider>
            <SkillMatrixSettingsPage />
          </AntdRlUISwitcherProvider>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_GROUPS}/${ROUTE_PATHS.MATCH_ALL}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="SETTINGS_TEAM_MEMBER:READ">
          <SettingsPageLayout
            checkIfCurrentUserIsAccountAdmin
            checkIfRbacIsEnabled
          >
            <AntdRlUISwitcherProvider>
              <GroupsCreationPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
      // children: [

      // ],
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_GROUPS}/${ROUTE_PATHS.SETTINGS_GROUP_DETAILS}/${ROUTE_PATHS.MATCH_ALL}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="SETTINGS_TEAM_MEMBER:READ">
          <SettingsPageLayout
            checkIfCurrentUserIsAccountAdmin
            checkIfRbacIsEnabled
          >
            <GroupDetail />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_ROLES}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_ROLE:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <RolesSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_PERMISSIONS,
      element: (
        <SecureRoute>
          <SettingsPageLayout
            checkIfCurrentUserIsAccountAdmin
            checkIfRbacIsEnabled
          >
            <PermisionHomePage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
      caseSensitive: true,
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PERMISSIONS}/${ROUTE_PATHS.SETTINGS_NEW_PERMISSION_SET}`,
      element: (
        <SecureRoute>
          <PermissionCreationPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PERMISSIONS}/${ROUTE_PATHS.SETTINGS_EDIT_PERMISSION_SET}`,
      element: (
        <SecureRoute>
          <PermissionCreationPage />
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_CONFIG,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOMER_PORTAL:READ">
          <SettingsPageLayout>
            <CustomerNotificationSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
      children: [
        {
          path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_PROJECT_TABS,
          element: <ProjectTabsVisibility />,
        },
        {
          path: ROUTE_PATHS.SETTINGS_CUSTOMER_PORTAL_CLIENT_NOTIFICATIONS,
          element: <NotificationsConfigPage customerConfig />,
        },
      ],
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PARTNERS}/*`,
      element: (
        <SecureRoute rule="SETTINGS_PARTNER:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <PartnerList />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
      caseSensitive: true,
      children: [
        // {
        //   path: 'new-partner',
        //   caseSensitive: true,
        //   element: <NewPartnerTypeSelection />,
        // },
        // {
        //   path: ROUTE_PATHS.NEW,
        //   caseSensitive: true,
        //   element: <NewPartner />,
        // },
      ],
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PARTNERS}/${ROUTE_PATHS.ID}/${ROUTE_PATHS.VIEW}`,
      element: (
        <SecureRoute rule="SETTINGS_PARTNER:READ">
          <PartnerDetail />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PARTNERS}/${ROUTE_PATHS.ID}/${ROUTE_PATHS.TEAM_MEMBERS}`,
      element: (
        <SecureRoute rule="SETTINGS_PARTNER:READ">
          <PartnerTeamMembers />
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_INTEGRATIONS,
      caseSensitive: true,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <SettingsPageLayout>
            <Integrations />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_JIRA_INTEGRATION}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <Jira />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_ZAPIER_INTEGRATION}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <Zapier />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_WORKATO_INTEGRATION}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <Workato />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_SALESFORCE_INTEGRATION}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <SalesForceIntegrationsLandingPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_SLACK_INTEGRATION}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <SlackLandingPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_INTEGRATIONS}/${ROUTE_PATHS.SETTINGS_HUBSPOT_INTEGRATION}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_INTEGRATION:READ">
          <HubspotIntegrationsLandingPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.TAXES}/${ROUTE_PATHS.SETTINGS}`,
      element: (
        <SecureRoute rule="SETTINGS_TAX:CREATE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TaxSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.TAXES}/${ROUTE_PATHS.TAXES_CODES}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="TAXCODE_AND_TAXCOMPONENT:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TaxCodes />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_TIMESHEETS,
      element: (
        <SecureRoute rule="SETTINGS_TIME_SHEET:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TimesheetSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EXPENSE_MANAGEMENT}/${ROUTE_PATHS.ESSENTIALS}`,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ExpenseSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EXPENSE_MANAGEMENT}/${ROUTE_PATHS.APPROVALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_EXPENSE_APPROVAL_WORKFLOW:CREATE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ExpenseApprovalSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_RESOURCE_MANAGEMENT,
      element: (
        <SecureRoute rule="SETTINGS_RESOURCE_MANAGEMENT:READ">
          <SettingsPageLayout>
            <ResourceManagementSettings />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_FINANCIALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_FINANCIAL:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <Financials />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_REVENUE}/${ROUTE_PATHS.REVENUE_ESSENTIALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_REVENUE_ENTRY:READ">
          <SettingsPageLayout>
            <RevenueEssentials />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_REVENUE}/${ROUTE_PATHS.POSTING_PERIODS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_REVENUE_ENTRY:READ">
          <SettingsPageLayout>
            <PostingPeriods />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CURRENCY,
      element: (
        <SecureRoute rule="SETTINGS_CURRENCY:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CurrencySettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_RATE_CARDS,
      element: (
        <SecureRoute rule="RATE_CARD:READ">
          <SettingsPageLayout checkIfUserHasAccessToViewRateCards>
            <AntdRlUISwitcherProvider>
              <RateCardsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_SPRINT_PLANNING,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <SprintPlanningSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_SPRINT_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <SprintFields />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EPIC_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <EpicFields />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_PROJECT_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_PROJECT_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ProjectFieldsV2SettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_TASK_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_TASK_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TaskFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_TEAM_MEMBER_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_TEAM_MEMBER_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TeamMemberFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_BUDGET_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_PROJECT_FINANCIALS_BUDGET_FIELDS:READ">
          <SettingsPageLayout>
            <BudgetFieldsSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_TIME_ENTRY_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_TIME_ENTRY_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <TimeEntryFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_ALLOCATION_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AllocationFieldsSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EXPENSE_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_EXPENSE_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ExpenseFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_COMPANY_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOMER_COMPANY_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CompanyFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_SKILL_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_SKILL_FIELD:READ">
          <SettingsPageLayout checkIfUserHasAccessToSkillMatrix>
            <AntdRlUISwitcherProvider>
              <SkillFieldsSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_CUSTOM_DOMAIN,
      element: (
        <SecureRoute rule="SETTINGS_CUSTOM_DOMAIN:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomDomain />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_EMAIL_CONFIGURATION,
      element: (
        <SecureRoute rule="SETTINGS_EMAIL_CONFIGURATION:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <EmailConfiguration />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_SECURITY,
      element: (
        <SecureRoute rule="SETTINGS_SECURITY:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <SecurityPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_SCIM,
      element: (
        <SecureRoute rule="SETTINGS_SECURITY:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <ScimPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_VAULT,
      element: (
        <SecureRoute rule="SETTINGS_VAULT:WRITE">
          <SettingsPageLayout>
            <VaultPageWithFeatureFlag />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_API,
      element: (
        <SecureRoute rule="SETTINGS_ADVANCED:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <RocketlaneAPIPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_WEBHOOK,
      element: (
        <SecureRoute rule="PUBLIC_WEBHOOK:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <WebhookPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
      children: [
        {
          path: ROUTE_PATHS.NEW,
          element: <NewWebhookPage />,
        },
        {
          path: `${ROUTE_PATHS.WEBHOOK_ID}/${ROUTE_PATHS.EDIT}`,
          element: <EditWebhookPage />,
        },
      ],
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_KEY_EVENT_AND_INTERVALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_KEY_EVENTS_AND_INTERVALS:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <KeyeventsAndIntervalsSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_CUSTOM_APPS}/`,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CustomAppsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_AUTOMATIONS,
      element: (
        <SecureRoute rule="SETTINGS_AUTOMATION:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <GlobalAutomationSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_ADVANCED,
      element: (
        <SecureRoute rule="SETTINGS_ADVANCED:READ">
          <SettingsPageLayout>
            <AdvancedSettingsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_ARCHIVED_PROJECTS,
      element: (
        <SecureRoute>
          <SettingsPageLayout>
            <ArchivedProjectsPage />
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_AI_FILLS,
      element: (
        <SecureRoute rule="SETTINGS_AI_FILLS:WRITE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <AiFillSettingsPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SIGNAL_CONFIGURATIONS,
      element: (
        <SecureRoute rule="SIGNAL_CONFIGURATION:READ">
          <SignalConfigurationsPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EMAIL_INTELLIGENCE}/${ROUTE_PATHS.SETTINGS_EMAIL_SETTINGS}`,
      element: (
        <SecureRoute rule="EMAIL_INTELLIGENCE_SETTINGS:WRITE">
          <EmailSettingsPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_EMAIL_INTELLIGENCE}/${ROUTE_PATHS.SETTINGS_BLACKLISTED_DOMAINS}`,
      element: (
        <SecureRoute rule="EMAIL_INTELLIGENCE_SETTINGS:WRITE">
          <BlacklistedDomainsPage />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.SETTINGS_KNOWLEDGE_BASE}/${ROUTE_PATHS.KNOWLEDGE_BASE_FOLDER}`,
      element: (
        <SecureRoute>
          <KnowledgeBaseFeatureWrapper>
            <KnowledgeBaseFolderPage />
          </KnowledgeBaseFeatureWrapper>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_KNOWLEDGE_BASE,
      element: (
        <SecureRoute>
          <KnowledgeBaseFeatureWrapper>
            <KnowledgeBasePage />
          </KnowledgeBaseFeatureWrapper>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SETTINGS_LANGUAGE_CONFIGURATIONS,
      element: (
        <SecureRoute>
          <AntdRlUISwitcherProvider>
            <LanguageConfigurationsPage />
          </AntdRlUISwitcherProvider>
        </SecureRoute>
      ),
    },
    // invoice/essentials
    {
      path: `${ROUTE_PATHS.INVOICE}/${ROUTE_PATHS.ESSENTIALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_INVOICE:CREATE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <InvoiceEssentialSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.INVOICE}/${ROUTE_PATHS.BILLING_SCHEDULE_TEMPLATE}`,
      element: (
        <SecureRoute rule="BILLING_EVENT_TEMPLATE:WRITE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <BillingScheduleTemplatesListPage />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.INVOICE}/${ROUTE_PATHS.BILLING_SCHEDULE_TEMPLATE}/${ROUTE_PATHS.NEW}`,
      element: (
        <SecureRoute rule="BILLING_EVENT_TEMPLATE:WRITE">
          <SettingsPageLayout>
            <Suspense fallback={null}>
              <BillingScheduleProviderShell>
                <NewBillingScheduleTemplatePage />
              </BillingScheduleProviderShell>
            </Suspense>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.INVOICE}/${ROUTE_PATHS.BILLING_SCHEDULE_TEMPLATE}/${ROUTE_PATHS.TEMPLATE_ID}`,
      element: (
        <SecureRoute rule="BILLING_EVENT_TEMPLATE:WRITE">
          <SettingsPageLayout>
            <Suspense fallback={null}>
              <BillingScheduleProviderShell>
                <ExistingBillingScheduleTemplatePage />
              </BillingScheduleProviderShell>
            </Suspense>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      // invoice/approvals
      path: `${ROUTE_PATHS.INVOICE}/${ROUTE_PATHS.APPROVALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_INVOICE_APPROVAL_WORKFLOW:CREATE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <InvoiceApprovalSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      // path: RoutePaths.invoiceCustomFieldsSettings,
      // invoice/custom-fields
      path: `${ROUTE_PATHS.SETTINGS_INVOICE_CREDIT_NOTES_CUSTOM_FIELDS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_INVOICE_FIELD:READ">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <InvoicingCustomFields />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.CREDIT_NOTES}/${ROUTE_PATHS.ESSENTIALS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: (
        <SecureRoute rule="SETTINGS_CREDIT_NOTE:WRITE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <CreditNoteEssentialSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    // {
    //   // credit-notes/approvals
    //   path: `${ROUTE_PATHS.CREDIT_NOTES}/${ROUTE_PATHS.APPROVALS}/${ROUTE_PATHS.MATCH_ALL}`,
    //   element: (
    //     // <SecureRoute rule="SETTINGS_CREDIT_NOTE_APPROVAL_WORKFLOW:CREATE">
    //     <SettingsPageLayout>
    //       <CreditNoteApprovalSettings />
    //     </SettingsPageLayout>
    //     // </SecureRoute>
    //   ),
    // },
    {
      // path: RoutePaths.addressSettings,
      // address
      path: ROUTE_PATHS.ADDRESS,
      element: (
        <SecureRoute rule="SETTINGS_ACCOUNT_ADDRESS:CREATE">
          <SettingsPageLayout>
            <AntdRlUISwitcherProvider>
              <AccountAddressSettings />
            </AntdRlUISwitcherProvider>
          </SettingsPageLayout>
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.MATCH_ALL,
      element: <NotFound />,
    },
  ],
};
